/**
 * ProductDetailsStep Component - Story 6.3: Create Add Product Form
 * Step 1: Product details form (name, category, description)
 */

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  productDetailsSchema,
  type ProductDetailsSchemaType,
} from '../../../schemas/productForm.schema'
import { ProductCategory } from '../../../types/productForm.types'
import { PRODUCT_CATEGORIES } from '../../../constants/productFormConstants'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Form,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

export interface ProductDetailsStepProps {
  formData: Partial<ProductDetailsSchemaType>
  errors: Record<string, string>
  onChange: (data: Partial<ProductDetailsSchemaType>) => void
  isValidating?: boolean
}

/**
 * ProductDetailsStep Component
 * Form step for entering basic product information
 *
 * @component
 * @example
 * <ProductDetailsStep
 *   formData={{ name: '', category: undefined, description: '' }}
 *   errors={{}}
 *   onChange={handleChange}
 * />
 */
export function ProductDetailsStep({
  formData,
  errors,
  onChange,
  isValidating = false,
}: ProductDetailsStepProps) {
  const form = useForm<ProductDetailsSchemaType>({
    resolver: zodResolver(productDetailsSchema),
    mode: 'onChange',
    values: {
      name: formData.name || '',
      category: formData.category as ProductCategory,
      description: formData.description || '',
    },
  })

  const handleChange = (data: Partial<ProductDetailsSchemaType>) => {
    onChange(data)
  }

  return (
    <Card className="product-details-step">
      <CardHeader>
        <CardTitle>Product Details</CardTitle>
        <CardDescription>
          Provide basic information about your product
        </CardDescription>
      </CardHeader>

      <CardContent>
        <Form {...form}>
          <form className="space-y-6">
            {/* Product Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="product-name">Product Name <span className="text-red-500">*</span></FormLabel>
                  <FormControl>
                    <Input
                      id="product-name"
                      placeholder="Enter product name"
                      {...field}
                      onChange={(e) => {
                        field.onChange(e)
                        handleChange({ name: e.target.value })
                      }}
                      disabled={isValidating}
                      aria-invalid={!!errors['name']}
                      aria-describedby={errors['name'] ? 'name-error' : undefined}
                    />
                  </FormControl>
                  {errors['name'] && (
                    <FormMessage id="name-error">{errors['name']}</FormMessage>
                  )}
                </FormItem>
              )}
            />

            {/* Category */}
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="category">Category <span className="text-red-500">*</span></FormLabel>
                  <FormControl>
                    <Select
                      value={field.value || ''}
                      onValueChange={(value) => {
                        field.onChange(value as ProductCategory)
                        handleChange({ category: value as ProductCategory })
                      }}
                      disabled={isValidating}
                    >
                      <SelectTrigger
                        id="category"
                        aria-invalid={!!errors['category']}
                        aria-describedby={errors['category'] ? 'category-error' : undefined}
                      >
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {PRODUCT_CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  {errors['category'] && (
                    <FormMessage id="category-error">
                      {errors['category']}
                    </FormMessage>
                  )}
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="description">Description <span className="text-red-500">*</span></FormLabel>
                  <FormControl>
                    <Textarea
                      id="description"
                      placeholder="Describe your product (minimum 20 characters)"
                      rows={4}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e)
                        handleChange({ description: e.target.value })
                      }}
                      disabled={isValidating}
                      aria-invalid={!!errors['description']}
                      aria-describedby={
                        errors['description'] ? 'description-error' : undefined
                      }
                    />
                  </FormControl>
                  {errors['description'] && (
                    <FormMessage id="description-error">
                      {errors['description']}
                    </FormMessage>
                  )}
                </FormItem>
              )}
            />
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
